import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import DatePicker from "react-datepicker";
import { enGB } from "date-fns/locale";
import styles from "./ReportsTable.module.scss";
import {
  CreditCalenderIcon,
  CreditFilterIcon,
  CreditReportEyeIcon,
  DateArrowLeft,
  DateArrowRight,
  SearchIcon,
} from "customIcons";
import FilterDropDown from "../filterDropdown/FilterDropDown";
import ReportsDetailModal from "../reportsDetailModal/ReportsDetailModal";
import { ReportDetails } from "interfaces/common.interface";
import { CloseIcon } from "@features/common/SvgIcons";
import { formatDate, formatTime } from "@utils/common";
import dayjs from "dayjs";
import EmptyTable from "@features/common/emptyTable/EmptyTable";

// ============================================================================
// Constants
// ============================================================================
const statusOptions = [
  { label: "All", value: "all" },
  { label: "Completed", value: "success" },
  { label: "Partially Completed", value: "completed" },
  { label: "In Progress", value: "in_progress" },
  { label: "Failed", value: "failed" },
];

// ============================================================================
// Types & Interfaces
// ============================================================================
interface ReportsTableProps {
  data?: any;
  isLoading?: boolean;
  searchTerm?: string;
  onSearchChange?: (value: string) => void;
  statusFilter?: string | null;
  onStatusFilterChange?: (value: string | null) => void;
  startDate?: Date | null;
  endDate?: Date | null;
  onDateRangeChange?: (start: Date | null, end: Date | null) => void;
}

// ============================================================================
// Helper Functions
// ============================================================================
/**
 * Returns the appropriate color for a given status
 *
 * @param {string} status - The status string to get color for
 * @returns {string} Hex color code for the status
 */
const getStatusColor = (status: string): string => {
  switch (status) {
    case "Completed":
      return "#65CF21";
    case "In Progress":
      return "#F70";
    case "Partially Completed":
      return "#2A88FF";
    case "Failed":
      return "#E74848";
    default:
      return "#0E0F0C";
  }
};

const ReportsTable: React.FC<ReportsTableProps> = ({
  data,
  isLoading,
  searchTerm = "",
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  startDate: propStartDate,
  endDate: propEndDate,
  onDateRangeChange,
}) => {
  // ============================================================================
  // Local State
  // ============================================================================
  const [isOpen, setIsOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<ReportDetails | null>(
    null,
  );
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    propStartDate || null,
    propEndDate || null,
  ]);
  const [startDate, endDate] = dateRange;
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  // ============================================================================
  // Refs
  // ============================================================================
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dateTriggerRef = useRef<HTMLDivElement | null>(null);
  const datePopoverRef = useRef<HTMLDivElement | null>(null);

  // ============================================================================
  // Effects
  // ============================================================================
  /**
   * Sync local date range with prop changes
   */
  useEffect(() => {
    setDateRange([propStartDate || null, propEndDate || null]);
  }, [propStartDate, propEndDate]);

  /**
   * Handle clicks outside date picker to close it
   */
  useEffect(() => {
    if (!isDatePickerOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const clickedInsidePopover =
        datePopoverRef.current && datePopoverRef.current.contains(target);
      const clickedOnTrigger =
        dateTriggerRef.current && dateTriggerRef.current.contains(target);

      if (!clickedInsidePopover && !clickedOnTrigger) {
        setIsDatePickerOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDatePickerOpen]);

  // ============================================================================
  // Event Handlers
  // ============================================================================
  /**
   * Opens the detail modal with selected report data
   *
   * @param {any} report - The report data to display in detail
   */
  const handleOpenDetailModal = (report: any) => {
    setIsDetailModalOpen(true);
    setSelectedReport({
      id: report.id,
      promotion: report.promotion,
      submitted_on: report.submitted_on,
      quantity: report.quantity,
      gross_amount: report.gross_amount,
      currency: report.currency,
    });
  };

  /**
   * Closes the detail modal
   */
  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
  };

  /**
   * Opens the date picker
   */
  const openDatePicker = useCallback(() => {
    setIsDatePickerOpen(true);
  }, []);

  /**
   * Handles mouse enter event for status filter dropdown
   * Clears any pending close timeout and opens the dropdown
   */
  const handleMouseEnter = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsOpen(true);
  }, []);

  /**
   * Handles mouse leave event for status filter dropdown
   * Sets a timeout to close the dropdown after 300ms
   */
  const handleMouseLeave = useCallback(() => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 300);
  }, []);

  // ============================================================================
  // Render
  // ============================================================================
  return (
    <div className={`${styles["reports-table"]} reports-table`}>
      {/* Action Bar with Filters */}
      <div className={styles["reports-table__action-bar"]}>
        <div className={styles["reports-table__action-bar__left"]}>Summary</div>
        <div className={styles["reports-table__action-bar__right"]}>
          {/* Search Input */}
          <div className={styles["reports-table__action-bar__right__search"]}>
            <SearchIcon />
            <input
              type="text"
              placeholder="Search by Credit Order ID/Promotion Name"
              className={
                styles["reports-table__search-bar-container__inner-input"]
              }
              value={searchTerm}
              onChange={(e) => onSearchChange?.(e.target.value)}
            />
            {searchTerm && (
              <div
                className={
                  styles["reports-table__action-bar__right__search__close-icon"]
                }
                onClick={() => onSearchChange?.("")}
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  width: "20px",
                }}
              >
                <CloseIcon />
              </div>
            )}
          </div>

          {/* Status Filter Dropdown */}
          <div
            className={styles["reports-table__action-bar__right__filter"]}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <CreditFilterIcon />
            <span>
              {statusFilter && statusFilter !== "all"
                ? statusOptions.find((option) => option.value === statusFilter)
                    ?.label
                : "Filter by status"}
            </span>
            {isOpen && (
              <FilterDropDown
                options={statusOptions}
                selectedValue={statusFilter}
                onSelect={onStatusFilterChange}
                setIsOpen={setIsOpen}
              />
            )}
            {!statusFilter ||
              (statusFilter !== "all" && (
                <div
                  className={
                    styles[
                      "reports-table__action-bar__right__filter__close-icon"
                    ]
                  }
                  onClick={() => {
                    setIsOpen(false);
                    onStatusFilterChange?.("all");
                  }}
                >
                  <CloseIcon />
                </div>
              ))}
          </div>

          {/* Date Range Picker */}
          <div
            className={styles["reports-table__action-bar__right__date"]}
            onClick={openDatePicker}
            role="button"
            tabIndex={0}
            ref={dateTriggerRef}
          >
            <CreditCalenderIcon />
            <span>
              {startDate || endDate
                ? `${startDate ? dayjs(startDate).format("DD MMM YYYY") : ""}${
                    startDate && endDate ? " - " : ""
                  }${endDate ? dayjs(endDate).format("DD MMM YYYY") : ""}`
                : "Filter by Date"}
            </span>
            {(startDate || endDate) && (
              <div
                className={
                  styles["reports-table__action-bar__right__date__close-icon"]
                }
                onClick={(e) => {
                  e.stopPropagation();
                  setDateRange([null, null]);
                  onDateRangeChange?.(null, null);
                  setIsDatePickerOpen(false);
                }}
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  width: "20px",
                }}
              >
                <CloseIcon />
              </div>
            )}
            {isDatePickerOpen && (
              <div
                className={
                  styles["reports-table__action-bar__right__date__date-picker"]
                }
                onClick={(e) => e.stopPropagation()}
                ref={datePopoverRef}
              >
                <DatePicker
                  selectsRange
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => {
                    const [start, end] = update as [Date | null, Date | null];
                    setDateRange([start, end]);
                    if (start && end) {
                      onDateRangeChange?.(start, end);
                      setIsDatePickerOpen(false);
                    }
                  }}
                  inline
                  locale={enGB}
                  formatWeekDay={(name) => name.slice(0, 3)}
                  renderCustomHeader={({
                    date,
                    decreaseMonth,
                    increaseMonth,
                    prevMonthButtonDisabled,
                    nextMonthButtonDisabled,
                  }) => (
                    <div className="custom-header-container">
                      <button
                        onClick={decreaseMonth}
                        disabled={prevMonthButtonDisabled}
                        className="nav-arrow"
                      >
                        <DateArrowLeft />
                      </button>

                      <span className="current-month">
                        {date.toLocaleString("default", {
                          month: "long",
                          year: "numeric",
                        })}
                      </span>

                      <button
                        onClick={increaseMonth}
                        disabled={nextMonthButtonDisabled}
                        className="nav-arrow"
                      >
                        <DateArrowRight />
                      </button>
                    </div>
                  )}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Reports Table */}

      <div
        className={styles["reports-table__table-wrapper"]}
        style={isLoading ? { height: "393px" } : { maxHeight: "393px" }}
      >
        <TableContainer>
          <Table className={styles["reports-table__table"]}>
            {/* Table Header */}
            <TableHead>
              <TableRow className={styles["reports-table__head-row"]}>
                <TableCell>Credit Order ID</TableCell>
                <TableCell>Promotion Name</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Total Credit Value</TableCell>
                <TableCell>Credit Date/Time</TableCell>
                <TableCell>Status</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>

            {/* Table Body with Loading State */}
            {isLoading ? (
              <CircularProgress
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              />
            ) : data?.data?.length > 0 ? (
              <TableBody>
                {/* <PageLoader /> */}
                {data?.data?.map((report: any, index: number) => (
                  <TableRow
                    key={index}
                    className={styles["reports-table__body-row"]}
                    sx={{ height: "84px" }}
                  >
                    {/* Credit Order ID */}
                    <TableCell>{report?.id}</TableCell>

                    {/* Promotion Name */}
                    <TableCell>{report.promotion}</TableCell>

                    {/* Quantity */}
                    <TableCell>{report.quantity}</TableCell>

                    {/* Total Credit Value */}
                    <TableCell>
                      {report?.currency} {report?.gross_amount}
                    </TableCell>

                    {/* Credit Date/Time */}
                    <TableCell>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <span>
                          {formatDate(report?.submitted_on)}
                        </span>
                        <span style={{ color: "#868785", fontWeight: "500" }}>
                          {formatTime(report?.submitted_on, "Asia/Dubai", {
                            ampm: true,
                          })}{" "}
                          UAE
                        </span>
                      </div>
                    </TableCell>

                    {/* Status */}
                    <TableCell style={{ color: getStatusColor(report.status) }}>
                      {report.status}
                    </TableCell>

                    {/* View Details Action */}
                    <TableCell>
                      <Tooltip
                        title="View Details"
                        placement="bottom"
                        arrow
                        componentsProps={{
                          tooltip: {
                            sx: {
                              backgroundColor: "#000",
                              color: "#fff",
                              fontSize: "10px",
                              padding: "4px 8px",
                              borderRadius: "4px",
                            },
                          },
                          arrow: { sx: { color: "#000" } },
                        }}
                      >
                        <div
                          style={{
                            cursor: "pointer",
                            display: "inline-flex",
                          }}
                          onClick={() => handleOpenDetailModal(report)}
                        >
                          <CreditReportEyeIcon />
                        </div>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            ) : (
              <EmptyTable colSpan={7} />
            )}
          </Table>
        </TableContainer>

        {/* Detail Modal */}
        {isDetailModalOpen && (
          <ReportsDetailModal
            onClose={handleCloseDetailModal}
            report={selectedReport ?? ({} as ReportDetails)}
          />
        )}
      </div>
    </div>
  );
};

export default ReportsTable;
